import { useState, useEffect } from 'react';
import { Copy } from 'lucide-react';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';

// datetime-local inputs take "YYYY-MM-DDTHH:MM"
const toInputValue = (isoString) => (isoString ? isoString.substring(0, 16) : '');

const shiftBy = (inputValue, deltaMs) => {
  if (!inputValue) return '';
  const shifted = new Date(new Date(inputValue).getTime() + deltaMs);
  const pad = (n) => String(n).padStart(2, '0');
  return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}-${pad(shifted.getDate())}T${pad(shifted.getHours())}:${pad(shifted.getMinutes())}`;
};

const DuplicateEventModal = ({ isOpen, onClose, event, onDuplicated }) => {
  const { currentOrganization } = useAppState();
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    registration_start_date: '',
    registration_end_date: ''
  });
  // Once the user edits a registration date by hand we stop auto-shifting it
  const [registrationTouched, setRegistrationTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (isOpen && event) {
      setFormData({
        name: event.name || '',
        date: toInputValue(event.date),
        registration_start_date: toInputValue(event.registration_start_date),
        registration_end_date: toInputValue(event.registration_end_date)
      });
      setRegistrationTouched(false);
      setError(null);
      setFieldErrors({});
    }
  }, [isOpen, event]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldErrors(prev => ({ ...prev, [name]: undefined }));

    if (name === 'date' && !registrationTouched && formData.date && value) {
      // Move the registration window by the same amount as the event date
      const deltaMs = new Date(value).getTime() - new Date(formData.date).getTime();
      setFormData(prev => ({
        ...prev,
        date: value,
        registration_start_date: shiftBy(prev.registration_start_date, deltaMs),
        registration_end_date: shiftBy(prev.registration_end_date, deltaMs)
      }));
      return;
    }

    if (name === 'registration_start_date' || name === 'registration_end_date') {
      setRegistrationTouched(true);
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Event name is required.';
    if (!formData.date) errors.date = 'Event date is required.';
    if (!formData.registration_start_date) errors.registration_start_date = 'Registration start is required.';
    if (!formData.registration_end_date) errors.registration_end_date = 'Registration end is required.';
    if (
      formData.registration_start_date &&
      formData.registration_end_date &&
      formData.registration_start_date >= formData.registration_end_date
    ) {
      errors.registration_end_date = 'Registration end must be after registration start.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post(
        `/organizations/${currentOrganization.id}/events/${event.id}/duplicate/`,
        formData
      );
      onDuplicated(response.data);
    } catch (err) {
      console.error('Error duplicating event:', err);
      const data = err.response?.data;
      if (err.response?.status === 400 && data && typeof data === 'object') {
        // DRF returns {field: [messages]}
        setFieldErrors(
          Object.fromEntries(Object.entries(data).map(([field, messages]) => [field, [].concat(messages).join(' ')]))
        );
      } else {
        setError(data?.message || 'Failed to duplicate event. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !event) return null;

  const inputClass = (field) =>
    `mt-1 block w-full rounded-md border p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
      fieldErrors[field] ? 'border-red-400' : 'border-gray-300'
    }`;

  const renderFieldError = (field) =>
    fieldErrors[field] ? <p className="mt-1 text-sm text-red-600">{fieldErrors[field]}</p> : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center">
            <Copy size={20} className="mr-2 text-indigo-600" />
            Duplicate "{event.name}"
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" disabled={submitting}>
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        <div className="mb-6 p-4 bg-indigo-50 border border-indigo-100 rounded-md text-sm text-gray-700">
          <p className="font-medium text-gray-900 mb-2">
            This creates a new occurrence of the event with the dates you choose. Copied over:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>All settings: form, email templates, ticket & badge designs, images, approval and payment options</li>
            <li>Ticket types, with fresh stock (nothing sold)</li>
            <li>Check-in zones, with activation windows shifted to the new dates</li>
            <li>Team access (event assignments)</li>
          </ul>
          <p className="mt-2 text-gray-600">
            Not copied: submissions, and the custom domain (a domain can only point at one event — assign it on the new
            event when ready).
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="duplicate-name" className="block text-sm font-medium text-gray-700">New Event Name</label>
            <input
              type="text"
              id="duplicate-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={inputClass('name')}
              required
            />
            {renderFieldError('name')}
          </div>

          <div>
            <label htmlFor="duplicate-date" className="block text-sm font-medium text-gray-700">Event Date</label>
            <input
              type="datetime-local"
              id="duplicate-date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className={inputClass('date')}
              required
            />
            {renderFieldError('date')}
            {!registrationTouched && (
              <p className="mt-1 text-xs text-gray-500">
                Changing the event date moves the registration window by the same amount.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="duplicate-registration-start" className="block text-sm font-medium text-gray-700">
                Registration Start
              </label>
              <input
                type="datetime-local"
                id="duplicate-registration-start"
                name="registration_start_date"
                value={formData.registration_start_date}
                onChange={handleChange}
                className={inputClass('registration_start_date')}
                required
              />
              {renderFieldError('registration_start_date')}
            </div>
            <div>
              <label htmlFor="duplicate-registration-end" className="block text-sm font-medium text-gray-700">
                Registration End
              </label>
              <input
                type="datetime-local"
                id="duplicate-registration-end"
                name="registration_end_date"
                value={formData.registration_end_date}
                onChange={handleChange}
                className={inputClass('registration_end_date')}
                required
              />
              {renderFieldError('registration_end_date')}
            </div>
          </div>

          <div className="flex justify-end pt-2 space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {submitting ? 'Duplicating...' : 'Duplicate Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DuplicateEventModal;
